/**
 * Inventory Master Report
 * 
 * Excel-like report showing comprehensive product inventory data with:
 * - Parent/child SKU hierarchy
 * - Multi-warehouse stock levels
 * - Regional breakdowns
 * - COGS and pricing data
 * - Conditional formatting (low stock warnings)
 * - Export to Excel
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Refresh,
  FilterList,
  Settings,
  Inventory2,
  Warning,
  CheckCircle,
  Business,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  EditNote as BulkEditIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import DataTable from '../common/DataTable';
import { useBrand } from '../../context/BrandContext';
import axios from 'axios';
import EntityActionsMenu from '../common/EntityActionsMenu';
import BulkImportDialog from '../common/BulkImportDialog';
import * as categoryService from '../../services/categoryService';

const InventoryMasterReport = () => {
  const { selectedBrand, brands, selectBrand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [adjustedVelocityStatus, setAdjustedVelocityStatus] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Product Master bulk-actions state — brand-scoped.
  const [pmImportOpen, setPmImportOpen] = useState(false);
  const [pmImportMode, setPmImportMode] = useState('insert');
  const [filters, setFilters] = useState({
    stockStatus: 'all', // all, in_stock, low_stock, out_of_stock
    productType: 'all',
    warehouse: 'all',
    category: 'all',
    subCategory: 'all',
  });

  // Category taxonomy + inline assignment state
  const [categoryTree, setCategoryTree] = useState({ categories: [] });
  const [assignRow, setAssignRow] = useState(null);
  const [assignCategoryVal, setAssignCategoryVal] = useState('');
  const [assignSubCategoryVal, setAssignSubCategoryVal] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState(null);

  // === Product Master bulk actions ====================================

  const handleExportProductMaster = async () => {
    if (!selectedBrand?.id) return;
    try {
      const res = await axios.get(
        `/api/brands/${selectedBrand.id}/products/master/export.csv`,
        { responseType: 'blob' }
      );
      let filename = `products-brand-${selectedBrand.id}-${new Date().toISOString().slice(0, 10)}.csv`;
      const cd = res.headers?.['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Export failed');
    }
  };

  const handlePmBulkImport = async (rows) => {
    if (!selectedBrand?.id) throw new Error('Select a brand first');
    if (pmImportMode === 'update') {
      const updates = rows.map(r => ({ ...r, id: parseInt(r.id, 10) }));
      const res = await axios.post(
        `/api/brands/${selectedBrand.id}/products/master/bulk-update`,
        { updates }
      );
      await fetchInventoryData();
      return res.data;
    }
    const res = await axios.post(
      `/api/brands/${selectedBrand.id}/products/master/bulk-import`,
      { rows }
    );
    await fetchInventoryData();
    return res.data;
  };

  // Column spec — mirrors backend PM_WRITABLE_* lists. Required for insert:
  // primary_sku + product_name (per backend pre-check).
  const PM_IMPORT_COLUMNS = [
    { field: 'primary_sku', label: 'Primary SKU', required: true,
      aliases: ['sku', 'code'] },
    { field: 'product_name', label: 'Product name', required: true,
      aliases: ['name', 'title'] },
    { field: 'primary_gtin', label: 'Primary GTIN', aliases: ['gtin', 'ean', 'upc'] },
    { field: 'product_type', label: 'Product type', aliases: ['type'] },
    { field: 'category', label: 'Category' },
    { field: 'product_level', label: 'Product level',
      enum: ['L0', 'L1', 'L2'],
      help: 'L0 = parts/raw, L1 = sellable finished good, L2 = bundle/combo' },
    { field: 'is_bundle', label: 'Is bundle?', type: 'boolean' },
    { field: 'is_prepacked', label: 'Is prepacked?', type: 'boolean' },
    { field: 'is_active', label: 'Active?', type: 'boolean' },
    { field: 'short_description', label: 'Short description', aliases: ['short_desc'] },
    { field: 'long_description', label: 'Long description', multiline: true, aliases: ['description', 'desc'] },
    { field: 'image_url', label: 'Image URL', aliases: ['image'] },
    { field: 'thumbnail_url', label: 'Thumbnail URL' },
    // Customs & origin
    { field: 'hs_code', label: 'HS code', aliases: ['hts_code', 'tariff_code'] },
    { field: 'country_of_origin', label: 'Country of origin', aliases: ['origin', 'coo'] },
    { field: 'customs_description', label: 'Customs description' },
    { field: 'customs_value', label: 'Customs value', type: 'number' },
    // Physical
    { field: 'weight_kg', label: 'Weight (kg)', type: 'number', aliases: ['weight'] },
    { field: 'weight_unit', label: 'Weight unit' },
    { field: 'length_cm', label: 'Length (cm)', type: 'number' },
    { field: 'width_cm', label: 'Width (cm)', type: 'number' },
    { field: 'height_cm', label: 'Height (cm)', type: 'number' },
    { field: 'dimension_unit', label: 'Dimension unit' },
    { field: 'volume_ml', label: 'Volume (ml)', type: 'number' },
    // Storage / handling
    { field: 'storage_conditions', label: 'Storage conditions' },
    { field: 'is_hazmat', label: 'Hazmat?', type: 'boolean' },
    { field: 'hazmat_class', label: 'Hazmat class' },
    { field: 'is_fragile', label: 'Fragile?', type: 'boolean' },
    { field: 'shelf_life_days', label: 'Shelf life (days)', type: 'number' },
    { field: 'requires_refrigeration', label: 'Needs refrigeration?', type: 'boolean' },
    // Supply
    { field: 'vendor', label: 'Vendor' },
    { field: 'manufacturer', label: 'Manufacturer' },
    { field: 'manufacturer_part_number', label: 'MPN', aliases: ['part_number'] },
    { field: 'lead_time_days', label: 'Lead time (days)', type: 'number' },
    { field: 'moq', label: 'MOQ', type: 'number' },
    { field: 'case_pack', label: 'Case pack', type: 'number' },
    // Pricing
    { field: 'msrp', label: 'MSRP', type: 'number' },
    { field: 'msrp_currency', label: 'MSRP currency', aliases: ['currency'] },
    // Misc
    { field: 'tags', label: 'Tags', help: 'Comma-separated list' },
    { field: 'notes', label: 'Notes' },
  ];

  const PM_IMPORT_SAMPLE = [
    {
      primary_sku: 'SS-FACE-50ML',
      product_name: 'Sand & Sky Brilliant Skin Purifying Pink Clay Mask 50ml',
      primary_gtin: '0123456789012',
      product_type: 'finished_good',
      category: 'Skincare / Masks',
      product_level: 'L1',
      is_bundle: 0,
      is_active: 1,
      short_description: 'Best-selling pink clay mask, travel size',
      hs_code: '3304.99.00',
      country_of_origin: 'AU',
      weight_kg: 0.065,
      weight_unit: 'kg',
      length_cm: 12, width_cm: 4, height_cm: 4,
      shelf_life_days: 1095,
      vendor: 'Acme Manufacturing Co.',
      lead_time_days: 90, moq: 5000, case_pack: 12,
      msrp: 24.95, msrp_currency: 'GBP',
    },
  ];

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
    }
  };

  useEffect(() => {
    if (selectedBrand) {
      fetchInventoryData();
      loadCategoryTree();
    }
  }, [selectedBrand]);

  const loadCategoryTree = async () => {
    if (!selectedBrand?.id) return;
    try {
      const data = await categoryService.getCategories(selectedBrand.id);
      setCategoryTree(data && Array.isArray(data.categories) ? data : { categories: [] });
    } catch (err) {
      console.error('Error loading category tree:', err);
      setCategoryTree({ categories: [] });
    }
  };

  // Sub-categories belonging to a given category name (from the brand tree).
  const subsForCategory = (catName) => {
    const node = (categoryTree.categories || []).find(c => c.category === catName);
    return node ? node.sub_categories.map(s => s.sub_category) : [];
  };

  // --- Inline category assignment ---------------------------------------
  const handleOpenAssign = useCallback((row) => {
    setAssignRow(row);
    setAssignCategoryVal(row.category || '');
    setAssignSubCategoryVal(row.subCategory || '');
    setAssignError(null);
  }, []);

  const handleCloseAssign = () => {
    if (assigning) return;
    setAssignRow(null);
    setAssignError(null);
  };

  // Submit a single product_master category update. `optimistic` is the
  // {category, subCategory} we apply to the row immediately for fast feedback;
  // the ALTER UPDATE is async server-side so we silently re-fetch shortly after.
  const submitAssign = async (update, optimistic) => {
    if (!assignRow || !selectedBrand?.id) return;
    setAssigning(true);
    setAssignError(null);
    try {
      const res = await categoryService.assignCategory(selectedBrand.id, [update]);
      if (res && res.failed > 0) {
        setAssignError(res.errors?.[0]?.error || 'Update failed');
        setAssigning(false);
        return;
      }
      const targetId = assignRow.id;
      setInventoryData(prev => prev.map(item =>
        item.id === targetId
          ? { ...item, category: optimistic.category, subCategory: optimistic.subCategory }
          : item
      ));
      setAssigning(false);
      setAssignRow(null);
      setTimeout(() => fetchInventoryData(true), 2500);
    } catch (err) {
      console.error('Error assigning category:', err);
      setAssignError(err.response?.data?.error || err.message || 'Update failed');
      setAssigning(false);
    }
  };

  const handleSaveAssign = () => {
    const category = assignCategoryVal;
    const subCategory = assignSubCategoryVal || '';
    submitAssign(
      { id: assignRow.id, category, sub_category: subCategory ? subCategory : '__CLEAR__' },
      { category, subCategory }
    );
  };

  const handleClearAssign = () => {
    submitAssign(
      { id: assignRow.id, category: '__CLEAR__', sub_category: '__CLEAR__' },
      { category: '', subCategory: '' }
    );
  };

  const fetchInventoryData = async (silent = false) => {
    if (!selectedBrand?.id) return;

    if (!silent) setLoading(true);
    setError(null);
    
    try {
      // Fetch data from multiple sources
      const adjustedVelocityReq = axios.get('/api/inventory-master/adjusted-velocity-summary', {
        params: { brand_id: selectedBrand.id },
      }).catch(err => ({
        data: {
          success: false,
          error: err.response?.data?.error || err.message || 'Adjusted velocity unavailable',
        },
      }));

      const [productsRes, stockRes, cogsRes, adjustedVelocityRes] = await Promise.all([
        axios.get(`/api/brands/${selectedBrand.id}/products/master`),
        axios.get(`/api/brands/${selectedBrand.id}/inventory/stock`),
        axios.get(`/api/brands/${selectedBrand.id}/products/cogs`),
        adjustedVelocityReq,
      ]);

      const adjustedVelocityReport = adjustedVelocityRes.data?.data || null;
      const signalStatus = adjustedVelocityReport
        ? {
            status: adjustedVelocityReport.adjusted_velocity_signal_status || 'unknown',
            error: adjustedVelocityReport.adjusted_velocity_signal_error || null,
          }
        : {
            status: 'error',
            error: adjustedVelocityRes.data?.error || 'Adjusted velocity unavailable',
          };
      setAdjustedVelocityStatus(signalStatus);

      // Transform and merge data
      const mergedData = transformInventoryData(
        productsRes.data,
        stockRes.data,
        cogsRes.data,
        adjustedVelocityReport
      );
      
      setInventoryData(mergedData);
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setError(err.response?.data?.message || 'Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  // Transform data into hierarchical structure with parent/child SKUs
  const transformInventoryData = (products, stock, cogs, adjustedVelocityReport) => {
    // Group products by primary SKU (parent)
    const grouped = {};
    const adjustedVelocityBySku = new Map(
      (adjustedVelocityReport?.rows || [])
        .filter(row => row.row_type === 'total' && row.sku)
        .map(row => [row.sku, row])
    );
    
    products.forEach(product => {
      const parentSku = product.primary_sku;
      
      if (!grouped[parentSku]) {
        grouped[parentSku] = {
          id: product.id,
          parentSku: parentSku,
          productName: product.product_name,
          productType: product.product_type || 'Standard',
          category: product.category || '',
          subCategory: product.sub_category || '',
          primaryGtin: product.primary_gtin || '',
          isBundle: product.is_bundle === 1,
          isPrepacked: product.is_prepacked === 1,
          isActive: product.is_active === 1,
          
          // Aggregate child SKU data
          childSkus: [],
          totalStock: 0,
          warehouses: {},
          cogs: 0,
          avgPrice: 0,
          planningDailyVelocity: 0,
          velocityConfidence: 'UNKNOWN',
          velocityReason: 'insufficient_data',
        };
      }
      
      // Add child SKU if it exists
      if (product.identifier_value && product.identifier_value !== parentSku) {
        grouped[parentSku].childSkus.push({
          sku: product.identifier_value,
          type: product.identifier_type,
          isPrimary: product.is_primary === 1,
          marketplace: product.marketplace || '',
        });
      }
    });
    
    // Add stock data
    stock.forEach(stockItem => {
      const parentSku = stockItem.parent_sku || stockItem.sku;
      if (grouped[parentSku]) {
        const warehouse = stockItem.warehouse_location || 'Main';
        if (!grouped[parentSku].warehouses[warehouse]) {
          grouped[parentSku].warehouses[warehouse] = 0;
        }
        grouped[parentSku].warehouses[warehouse] += parseInt(stockItem.quantity) || 0;
        grouped[parentSku].totalStock += parseInt(stockItem.quantity) || 0;
      }
    });
    
    // Add COGS data
    cogs.forEach(cogsItem => {
      const parentSku = cogsItem.primary_sku || cogsItem.sku;
      if (grouped[parentSku]) {
        grouped[parentSku].cogs = parseFloat(cogsItem.cogs) || 0;
        grouped[parentSku].avgPrice = parseFloat(cogsItem.price) || 0;
      }
    });

    adjustedVelocityBySku.forEach((velocityRow, sku) => {
      if (grouped[sku]) {
        grouped[sku].planningDailyVelocity = parseFloat(velocityRow.planning_daily_velocity) || 0;
        grouped[sku].velocityConfidence = velocityRow.velocity_confidence || 'UNKNOWN';
        grouped[sku].velocityReason = velocityRow.velocity_reason || 'insufficient_data';
      }
    });
    
    return Object.values(grouped);
  };

  // Define columns with Excel-like styling
  const columns = useMemo(() => [
    {
      id: 'parentSku',
      header: (
        <Tooltip title="Primary product SKU used for reporting and inventory tracking">
          <Box component="span">Parent SKU</Box>
        </Tooltip>
      ),
      accessorKey: 'parentSku',
      sticky: true,
      size: 150,
      cell: ({ row, getValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {getValue()}
          </Typography>
          {row.original.childSkus.length > 0 && (
            <Chip 
              label={`+${row.original.childSkus.length}`} 
              size="small"
              sx={{ height: 18, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'productName',
      header: (
        <Tooltip title="Full product name as stored in the system">
          <Box component="span">Product Name</Box>
        </Tooltip>
      ),
      accessorKey: 'productName',
      size: 250,
      wrap: true,
      cell: ({ getValue }) => (
        <Tooltip title={getValue()}>
          <Typography variant="body2" noWrap>
            {getValue()}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'gtin',
      header: (
        <Tooltip title="Global Trade Item Number - barcode identifier">
          <Box component="span">GTIN/Barcode</Box>
        </Tooltip>
      ),
      accessorKey: 'primaryGtin',
      size: 140,
      cell: ({ getValue }) => (
        <Typography variant="body2" fontFamily="monospace">
          {getValue() || '-'}
        </Typography>
      ),
    },
    {
      id: 'productType',
      header: (
        <Tooltip title="Product category and packaging type">
          <Box component="span">Product Type</Box>
        </Tooltip>
      ),
      accessorKey: 'productType',
      size: 150,
      cell: ({ getValue, row }) => {
        const type = getValue();
        const isBundle = row.original.isBundle;
        const isPrepacked = row.original.isPrepacked;
        
        return (
          <Stack direction="row" spacing={0.5}>
            <Chip 
              label={type || 'Standard'} 
              size="small" 
              variant="outlined"
            />
            {isBundle && (
              <Chip label="Bundle" size="small" color="info" />
            )}
            {isPrepacked && (
              <Chip label="Prepacked" size="small" color="secondary" />
            )}
          </Stack>
        );
      },
    },
    {
      id: 'category',
      header: (
        <Tooltip title="Product category (brand taxonomy)">
          <Box component="span">Category</Box>
        </Tooltip>
      ),
      accessorKey: 'category',
      size: 150,
      cell: ({ getValue }) => (
        <Typography variant="body2" noWrap>
          {getValue() || '-'}
        </Typography>
      ),
    },
    {
      id: 'subCategory',
      header: (
        <Tooltip title="Product sub-category (brand taxonomy)">
          <Box component="span">Sub-category</Box>
        </Tooltip>
      ),
      accessorKey: 'subCategory',
      size: 150,
      cell: ({ getValue }) => (
        <Typography variant="body2" noWrap>
          {getValue() || '-'}
        </Typography>
      ),
    },
    {
      id: 'totalStock',
      header: (
        <Tooltip title="Total units in stock across all warehouses">
          <Box component="span">Total Stock</Box>
        </Tooltip>
      ),
      accessorKey: 'totalStock',
      align: 'right',
      size: 120,
      cellStyle: (value, row) => {
        // Conditional formatting based on stock level
        if (value === 0) {
          return { bgcolor: '#ffebee', color: '#c62828', fontWeight: 600 };
        }
        if (value < 50) {
          return { bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 };
        }
        return { fontWeight: 600 };
      },
      cell: ({ getValue, row }) => {
        const value = getValue();
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            {value === 0 ? (
              <Warning fontSize="small" color="error" />
            ) : value < 50 ? (
              <Warning fontSize="small" color="warning" />
            ) : (
              <CheckCircle fontSize="small" color="success" />
            )}
            <Typography variant="body2" fontWeight={600}>
              {value.toLocaleString()}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'cogs',
      header: (
        <Tooltip title="Cost of Goods Sold - unit cost to produce/acquire">
          <Box component="span">Unit COGS</Box>
        </Tooltip>
      ),
      accessorKey: 'cogs',
      align: 'right',
      size: 110,
      cell: ({ getValue }) => (
        <Typography variant="body2" fontFamily="monospace">
          ${getValue().toFixed(2)}
        </Typography>
      ),
    },
    {
      id: 'planningDailyVelocity',
      header: (
        <Tooltip title="Planning daily velocity from adjusted velocity signals">
          <Box component="span">Planning Vel</Box>
        </Tooltip>
      ),
      accessorKey: 'planningDailyVelocity',
      align: 'right',
      size: 120,
      cell: ({ getValue }) => (
        <Typography variant="body2" fontFamily="monospace">
          {(Number(getValue()) || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      id: 'velocityConfidence',
      header: (
        <Tooltip title="Confidence level for the planning velocity signal">
          <Box component="span">Confidence</Box>
        </Tooltip>
      ),
      accessorKey: 'velocityConfidence',
      size: 120,
      cell: ({ getValue }) => {
        const confidence = getValue() || 'UNKNOWN';
        const color = confidence === 'HIGH' ? 'success' : confidence === 'MEDIUM' ? 'warning' : 'default';
        return <Chip label={confidence} color={color} size="small" variant="outlined" />;
      },
    },
    {
      id: 'velocityReason',
      header: (
        <Tooltip title="Reason code behind the planning velocity value">
          <Box component="span">Reason</Box>
        </Tooltip>
      ),
      accessorKey: 'velocityReason',
      size: 180,
      cell: ({ getValue }) => (
        <Typography variant="body2" noWrap>
          {getValue() || 'insufficient_data'}
        </Typography>
      ),
    },
    {
      id: 'avgPrice',
      header: (
        <Tooltip title="Average selling price across all channels">
          <Box component="span">Avg Selling Price</Box>
        </Tooltip>
      ),
      accessorKey: 'avgPrice',
      align: 'right',
      size: 140,
      cell: ({ getValue }) => (
        <Typography variant="body2" fontFamily="monospace">
          ${getValue().toFixed(2)}
        </Typography>
      ),
    },
    {
      id: 'margin',
      header: (
        <Tooltip title="Profit margin: (Price - COGS) / Price × 100">
          <Box component="span">Margin %</Box>
        </Tooltip>
      ),
      accessorFn: row => {
        if (row.avgPrice === 0) return 0;
        return ((row.avgPrice - row.cogs) / row.avgPrice) * 100;
      },
      align: 'right',
      size: 100,
      cellStyle: (value) => {
        if (value < 30) {
          return { color: '#c62828', fontWeight: 600 };
        }
        if (value > 50) {
          return { color: '#2e7d32', fontWeight: 600 };
        }
        return {};
      },
      cell: ({ getValue }) => (
        <Typography variant="body2" fontWeight={600}>
          {getValue().toFixed(1)}%
        </Typography>
      ),
    },
    {
      id: 'inventoryValue',
      header: (
        <Tooltip title="Total value of inventory at cost (Total Stock × COGS)">
          <Box component="span">Inventory Value</Box>
        </Tooltip>
      ),
      accessorFn: row => row.totalStock * row.cogs,
      align: 'right',
      size: 140,
      cell: ({ getValue }) => (
        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
          ${getValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      id: 'status',
      header: (
        <Tooltip title="Product availability status">
          <Box component="span">Status</Box>
        </Tooltip>
      ),
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }) => (
        <Chip
          label={getValue() ? 'Active' : 'Inactive'}
          color={getValue() ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'assign',
      header: '',
      size: 70,
      align: 'center',
      enableSorting: false,
      cell: ({ row }) => (
        <Tooltip title="Assign category">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleOpenAssign(row.original); }}
          >
            <CategoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [handleOpenAssign]);

  // Apply filters
  const filteredData = useMemo(() => {
    return inventoryData.filter(item => {
      // Stock status filter
      if (filters.stockStatus !== 'all') {
        if (filters.stockStatus === 'out_of_stock' && item.totalStock > 0) return false;
        if (filters.stockStatus === 'low_stock' && (item.totalStock === 0 || item.totalStock >= 50)) return false;
        if (filters.stockStatus === 'in_stock' && item.totalStock < 50) return false;
      }
      
      // Product type filter
      if (filters.productType !== 'all' && item.productType !== filters.productType) {
        return false;
      }

      // Category filter
      if (filters.category !== 'all' && item.category !== filters.category) {
        return false;
      }

      // Sub-category filter
      if (filters.subCategory !== 'all' && item.subCategory !== filters.subCategory) {
        return false;
      }

      return true;
    });
  }, [inventoryData, filters]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    return {
      totalProducts: filteredData.length,
      totalStock: filteredData.reduce((sum, item) => sum + item.totalStock, 0),
      totalValue: filteredData.reduce((sum, item) => sum + (item.totalStock * item.cogs), 0),
      outOfStock: filteredData.filter(item => item.totalStock === 0).length,
      lowStock: filteredData.filter(item => item.totalStock > 0 && item.totalStock < 50).length,
    };
  }, [filteredData]);

  if (!brands || brands.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No brands available. Please create a brand first.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Options for the assign dialog. Always include the row's current value so a
  // legacy/off-taxonomy category still renders in the Select (no MUI warning).
  const assignCategoryOptions = (() => {
    const opts = (categoryTree.categories || []).map(c => c.category);
    if (assignRow?.category && !opts.includes(assignRow.category)) opts.unshift(assignRow.category);
    return opts;
  })();
  const assignSubCategoryOptions = (() => {
    const opts = subsForCategory(assignCategoryVal);
    if (assignSubCategoryVal && !opts.includes(assignSubCategoryVal)) {
      return [assignSubCategoryVal, ...opts];
    }
    return opts;
  })();

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header with Brand Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <Business sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Inventory Master Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Comprehensive product inventory with SKU aliases, stock levels, and COGS data
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Brand</InputLabel>
              <Select
                value={selectedBrand?.id || ''}
                onChange={handleBrandChange}
                label="Select Brand"
              >
                {brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <EntityActionsMenu
              label="Actions"
              buttonProps={{ disabled: !selectedBrand?.id }}
              actions={[
                {
                  key: 'import-pm',
                  label: 'Import products',
                  hint: `CSV/Excel — adds new products to ${selectedBrand?.name || 'brand'}`,
                  icon: <ImportIcon fontSize="small" />,
                  group: 'Data',
                  onClick: () => { setPmImportMode('insert'); setPmImportOpen(true); },
                },
                {
                  key: 'export-pm',
                  label: 'Export product master',
                  hint: 'Downloads brand product_master as CSV (round-trippable)',
                  icon: <ExportIcon fontSize="small" />,
                  group: 'Data',
                  onClick: handleExportProductMaster,
                },
                {
                  key: 'update-pm-csv',
                  label: 'Update products from CSV',
                  hint: 'Export → edit in Excel → re-upload. Matches by id.',
                  icon: <BulkEditIcon fontSize="small" />,
                  group: 'Data',
                  onClick: () => { setPmImportMode('update'); setPmImportOpen(true); },
                },
              ]}
            />
          </Stack>
        </CardContent>
      </Card>

      {!selectedBrand ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a brand above to view inventory data
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <>
      {/* Summary Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Total Products</Typography>
          <Typography variant="h4">{summary.totalProducts}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Total Stock</Typography>
          <Typography variant="h4">{summary.totalStock.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Inventory Value</Typography>
          <Typography variant="h4">${summary.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, bgcolor: '#ffebee' }}>
          <Typography variant="body2" color="text.secondary">Out of Stock</Typography>
          <Typography variant="h4" color="error">{summary.outOfStock}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, bgcolor: '#fff3e0' }}>
          <Typography variant="body2" color="text.secondary">Low Stock</Typography>
          <Typography variant="h4" color="warning.main">{summary.lowStock}</Typography>
        </Paper>
      </Stack>

      {/* Category Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" color="text.secondary">
            Filter by category:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              label="Category"
              onChange={(e) => setFilters({ ...filters, category: e.target.value, subCategory: 'all' })}
            >
              <MenuItem value="all">All categories</MenuItem>
              {(categoryTree.categories || []).map(c => (
                <MenuItem key={c.category} value={c.category}>
                  {c.category} ({c.product_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 240 }} disabled={filters.category === 'all'}>
            <InputLabel>Sub-category</InputLabel>
            <Select
              value={filters.subCategory}
              label="Sub-category"
              onChange={(e) => setFilters({ ...filters, subCategory: e.target.value })}
            >
              <MenuItem value="all">All sub-categories</MenuItem>
              {subsForCategory(filters.category).map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(filters.category !== 'all' || filters.subCategory !== 'all') && (
            <Button
              size="small"
              onClick={() => setFilters({ ...filters, category: 'all', subCategory: 'all' })}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Data Table */}
      {adjustedVelocityStatus?.status === 'error' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Adjusted velocity signals are unavailable: {adjustedVelocityStatus.error}
        </Alert>
      )}
      <DataTable
        data={filteredData}
        columns={columns}
        title="Inventory Master Report"
        onRefresh={fetchInventoryData}
        enableExport={true}
        enableSearch={true}
        enableGrouping={false}
        exportFileName={`inventory_master_${selectedBrand.name}`}
        customToolbarActions={
          <Tooltip title="Filter Options">
            <IconButton onClick={() => setFilterDialogOpen(true)} size="small">
              <FilterList />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Options</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Stock Status</InputLabel>
              <Select
                value={filters.stockStatus}
                label="Stock Status"
                onChange={(e) => setFilters({ ...filters, stockStatus: e.target.value })}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="in_stock">In Stock (&gt;= 50)</MenuItem>
                <MenuItem value="low_stock">Low Stock (1-49)</MenuItem>
                <MenuItem value="out_of_stock">Out of Stock (0)</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={filters.productType}
                label="Product Type"
                onChange={(e) => setFilters({ ...filters, productType: e.target.value })}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="HERO">Hero Products</MenuItem>
                <MenuItem value="Standard">Standard</MenuItem>
                <MenuItem value="Bundle">Bundles</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setFilters({
                stockStatus: 'all',
                productType: 'all',
                warehouse: 'all',
                category: 'all',
                subCategory: 'all',
              });
              setFilterDialogOpen(false);
            }}
          >
            Reset Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Category Dialog */}
      <Dialog open={Boolean(assignRow)} onClose={handleCloseAssign} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Category</DialogTitle>
        <DialogContent>
          {assignRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {assignRow.parentSku} — {assignRow.productName}
              </Typography>
              {assignError && <Alert severity="error">{assignError}</Alert>}
              {assignCategoryOptions.length === 0 ? (
                <Alert severity="info">
                  No categories defined for this brand yet. Add categories before assigning.
                </Alert>
              ) : (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={assignCategoryVal}
                      label="Category"
                      onChange={(e) => { setAssignCategoryVal(e.target.value); setAssignSubCategoryVal(''); }}
                    >
                      {assignCategoryOptions.map(c => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth disabled={!assignCategoryVal}>
                    <InputLabel>Sub-category</InputLabel>
                    <Select
                      value={assignSubCategoryVal}
                      label="Sub-category"
                      onChange={(e) => setAssignSubCategoryVal(e.target.value)}
                    >
                      <MenuItem value=""><em>— None —</em></MenuItem>
                      {assignSubCategoryOptions.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={handleClearAssign} disabled={assigning || !assignRow}>
            Clear
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseAssign} disabled={assigning}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAssign}
            disabled={assigning || !assignCategoryVal}
          >
            {assigning ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      <BulkImportDialog
        open={pmImportOpen}
        onClose={() => setPmImportOpen(false)}
        entityLabel="product"
        mode={pmImportMode}
        columnSpec={PM_IMPORT_COLUMNS}
        sampleRows={PM_IMPORT_SAMPLE}
        onSubmit={handlePmBulkImport}
        helpText={
          pmImportMode === 'update'
            ? `Round-trip for ${selectedBrand?.name || 'brand'} products: keep id, edit any cells, leave blank to skip. __CLEAR__ explicitly empties a field.`
            : `Required: primary_sku + product_name. Header row required, column order doesn't matter, headers are case-insensitive (e.g. 'SKU' → primary_sku).`
        }
      />
    </Box>
  );
};

export default InventoryMasterReport;
